import React from 'react';
import { CheckCircle, AlertTriangle, Info, Loader2, Clock } from 'lucide-react';
import { FinancialValue } from '../../shared/FinancialValue';
import type { PhasedPreviewResult } from '../../../services/futureClosureApi';

interface Props {
  result: PhasedPreviewResult | null;
  loading: boolean;
  error: string | null;
  /** Show the recovery-period derivation evidence block (defaults true). */
  showRecoveryEvidence?: boolean;
}

export default function PhasedDamagesBreakdown({ result, loading, error, showRecoveryEvidence = true }: Props) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 cs-meta p-4" style={{ color: 'var(--color-text-muted)' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Computing phased pro-forma from your financials…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 cs-meta p-4" style={{ color: 'var(--color-text-muted)' }}>
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-warning, #b45309)' }} />
        <span>{error}</span>
      </div>
    );
  }
  if (!result) {
    return (
      <p className="cs-meta p-4" style={{ color: 'var(--color-text-muted)' }}>
        Phased pro-forma will compute once closure timing (Step 2) and financials are in place.
      </p>
    );
  }

  const components = result.components || [];

  return (
    <div className="space-y-4">
      {/* Itemized component table (only components that fired appear) */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
        <div className="grid grid-cols-12 px-4 py-2 cs-label" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
          <div className="col-span-6">Damage Component</div>
          <div className="col-span-3 text-right">Undiscounted</div>
          <div className="col-span-3 text-right">Present Value</div>
        </div>
        {components.length === 0 && (
          <div className="px-4 py-3 cs-meta" style={{ color: 'var(--color-text-muted)' }}>
            No damage components computed for the current inputs.
          </div>
        )}
        {components.map((c, i) => (
          <div
            key={`${c.phase_label}-${i}`}
            className="grid grid-cols-12 px-4 py-3 items-center"
            style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}
          >
            <div className="col-span-6">
              <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{c.phase_label}</p>
              <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>{c.month_or_span}</p>
            </div>
            <div className="col-span-3 text-right cs-figure" style={{ color: 'var(--color-text-secondary)' }}><FinancialValue value={c.gross} /></div>
            <div className="col-span-3 text-right cs-figure" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={c.pv} /></div>
          </div>
        ))}
        {/* Totals */}
        <div className="grid grid-cols-12 px-4 py-3 items-center" style={{ borderTop: '2px solid var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }}>
          <div className="col-span-6 cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>Total (Present Value)</div>
          <div className="col-span-3 text-right cs-figure" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={result.total_undiscounted} /></div>
          <div className="col-span-3 text-right cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-needs-human-text)', fontWeight: 700 }}><FinancialValue value={result.total_pv} /></div>
        </div>
      </div>

      {/* Data-derived recovery evidence (defensibility) */}
      {showRecoveryEvidence && (
        <div className="p-4 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
            <span className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>Recovery-Period Basis</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="cs-meta">Recovery period applied</span>
            <span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>
              {result.recovery_period_used != null ? `${result.recovery_period_used} months` : '—'}
            </span>
          </div>
          {result.recovery_period_source === 'data_derived' && (
            <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              <CheckCircle className="w-3.5 h-3.5" /> Derived from post-reopening data
              {result.goodness_of_fit != null && ` · R² ${result.goodness_of_fit.toFixed(2)}`}
            </span>
          )}
          {result.recovery_period_source === 'attorney_override' && (
            <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
              <Info className="w-3.5 h-3.5" /> Attorney override (not data-supported)
            </span>
          )}
          {result.recovery_period_source === 'projection_unsupported' && (
            <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
              <AlertTriangle className="w-3.5 h-3.5" /> Insufficient post-reopening data — recovery-period loss not projected
            </span>
          )}
          {result.permanent_closure && (
            <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
              <Info className="w-3.5 h-3.5" /> Permanent closure — damages via diminished business value (no recovery ramp)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
